import { useState, useEffect, useRef } from 'react';
import { categories } from '@/mocks/categories';
import { articlePreviews } from '@/mocks/articles';
import { Link } from 'react-router-dom';

export default function Navbar() {
  const [isCatOpen, setIsCatOpen] = useState(false);
  const [isGuideOpen, setIsGuideOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mobileActiveTab, setMobileActiveTab] = useState<'categories' | 'guides'>('categories');
  const catDropdownRef = useRef<HTMLDivElement>(null);
  const guideDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (catDropdownRef.current && !catDropdownRef.current.contains(e.target as Node)) {
        setIsCatOpen(false);
      }
      if (guideDropdownRef.current && !guideDropdownRef.current.contains(e.target as Node)) {
        setIsGuideOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="mx-auto px-4 md:px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4 md:gap-6">
          <Link
            to="/"
            className="flex items-center gap-2 text-foreground-900 hover:opacity-80 transition-opacity"
          >
            <span className="font-heading text-xl md:text-2xl font-bold whitespace-nowrap">
              oopbuyspread
            </span>
          </Link>
        </div>

        <div className="hidden md:flex items-center gap-3">
          <Link
            to="/"
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-foreground-600 hover:bg-background-100 hover:text-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-home-4-line"></i>
            </span>
            Startseite
          </Link>

          <div className="relative" ref={catDropdownRef}>
            <button
              onClick={() => {
                setIsCatOpen(!isCatOpen);
                setIsGuideOpen(false);
              }}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-foreground-700 bg-background-100 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-grid-line"></i>
              </span>
              <span>Kategorien</span>
              <span className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${isCatOpen ? 'rotate-180' : ''}`}>
                <i className="ri-arrow-down-s-line"></i>
              </span>
            </button>

            {isCatOpen && (
              <div className="absolute top-full mt-2 left-0 w-72 bg-background-50 border border-background-200 rounded-xl shadow-lg py-2 max-h-[420px] overflow-y-auto z-50">
                <div className="px-4 py-1.5 text-xs font-semibold text-foreground-400 uppercase tracking-wide">
                  Alle 14 Kategorien
                </div>
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
                  >
                    <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-foreground-50 text-foreground-500 flex-shrink-0">
                      <i className={`${cat.icon} text-base`}></i>
                    </span>
                    <span className="flex-1">{cat.name}</span>
                    <span className="w-4 h-4 flex items-center justify-center text-foreground-300">
                      <i className="ri-external-link-line text-xs"></i>
                    </span>
                  </a>
                ))}
              </div>
            )}
          </div>

          <div className="relative" ref={guideDropdownRef}>
            <button
              onClick={() => {
                setIsGuideOpen(!isGuideOpen);
                setIsCatOpen(false);
              }}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-foreground-700 bg-background-100 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-book-open-line"></i>
              </span>
              <span>Einkaufsguide</span>
              <span className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${isGuideOpen ? 'rotate-180' : ''}`}>
                <i className="ri-arrow-down-s-line"></i>
              </span>
            </button>

            {isGuideOpen && (
              <div className="absolute top-full mt-2 left-0 w-72 bg-background-50 border border-background-200 rounded-xl shadow-lg py-2 max-h-[420px] overflow-y-auto z-50">
                <div className="px-4 py-1.5 text-xs font-semibold text-foreground-400 uppercase tracking-wide">
                  Einkaufsguide Artikel
                </div>
                {articlePreviews.map((ap) => (
                  <Link
                    key={ap.slug}
                    to={`/artikel/${ap.slug}`}
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
                    onClick={() => setIsGuideOpen(false)}
                  >
                    <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-foreground-50 text-foreground-500 flex-shrink-0">
                      <i className="ri-article-line text-base"></i>
                    </span>
                    <div className="flex-1 min-w-0">
                      <span className="block text-xs font-medium truncate">{ap.title}</span>
                      <span className="block text-[10px] text-foreground-400">{ap.category} · {ap.readTime}</span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-primary-500 text-white hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            OOPBuy Spreadsheet
          </a>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-accent-500 text-white hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            Buy It Now
          </a>
        </div>

        <button
          onClick={() => {
            setIsMobileMenuOpen(!isMobileMenuOpen);
            setIsCatOpen(false);
            setIsGuideOpen(false);
          }}
          className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-700 cursor-pointer"
          aria-label="Menü"
        >
          <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
        </button>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200">
          <div className="flex border-b border-background-200">
            <button
              onClick={() => setMobileActiveTab('categories')}
              className={`flex-1 py-3 text-xs font-semibold text-center transition-colors cursor-pointer ${
                mobileActiveTab === 'categories'
                  ? 'text-primary-500 border-b-2 border-primary-500'
                  : 'text-foreground-400'
              }`}
            >
              <span className="w-4 h-4 inline-flex items-center justify-center mr-1">
                <i className="ri-grid-line"></i>
              </span>
              Kategorien
            </button>
            <button
              onClick={() => setMobileActiveTab('guides')}
              className={`flex-1 py-3 text-xs font-semibold text-center transition-colors cursor-pointer ${
                mobileActiveTab === 'guides'
                  ? 'text-primary-500 border-b-2 border-primary-500'
                  : 'text-foreground-400'
              }`}
            >
              <span className="w-4 h-4 inline-flex items-center justify-center mr-1">
                <i className="ri-book-open-line"></i>
              </span>
              Einkaufsguide
            </button>
          </div>

          <div className="px-4 py-3 max-h-[50vh] overflow-y-auto">
            {mobileActiveTab === 'categories' ? (
              <div className="grid grid-cols-2 gap-1.5">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <span className="w-7 h-7 flex items-center justify-center rounded-md bg-foreground-50 text-foreground-500 flex-shrink-0">
                      <i className={`${cat.icon} text-sm`}></i>
                    </span>
                    <span className="text-xs whitespace-nowrap">{cat.name}</span>
                  </a>
                ))}
              </div>
            ) : (
              <div className="flex flex-col gap-1">
                {articlePreviews.map((ap) => (
                  <Link
                    key={ap.slug}
                    to={`/artikel/${ap.slug}`}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <span className="w-7 h-7 flex items-center justify-center rounded-md bg-foreground-50 text-foreground-500 flex-shrink-0">
                      <i className="ri-article-line text-sm"></i>
                    </span>
                    <div className="min-w-0">
                      <span className="block text-xs font-medium truncate">{ap.title}</span>
                      <span className="block text-[10px] text-foreground-400">{ap.category}</span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          <div className="border-t border-background-200 px-4 py-3 flex flex-col gap-2">
            <Link
              to="/"
              className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-home-4-line"></i>
              </span>
              Startseite
            </Link>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-3 py-2.5 rounded-lg text-sm font-semibold bg-primary-500 text-white text-center cursor-pointer"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              OOPBuy Spreadsheet
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-3 py-2.5 rounded-lg text-sm font-semibold bg-accent-500 text-white text-center cursor-pointer"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Buy It Now
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}